import React, { useState, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { Menu, X, Code2, Linkedin, Mail } from 'lucide-react'

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false)
  const [scrolled, setScrolled] = useState(false)
  const [activeSection, setActiveSection] = useState('')
  const location = useLocation()

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20)
      
      // Scroll spy: determine which section is currently in view
      if (location.pathname === '/') {
        const sections = ['home', 'project-showcase', 'professional-journey', 'technical-skills']
        const scrollPosition = window.scrollY + 100 // Offset for navbar
        const windowHeight = window.innerHeight
        const documentHeight = document.documentElement.scrollHeight
        
        // Check if scrolled to bottom of page (within 150px of bottom) - only then highlight technical-skills
        if (window.scrollY + windowHeight >= documentHeight - 150) {
          setActiveSection('technical-skills')
          return
        }
        
        // Check sections from top to bottom to find which one is currently in view
        let activeFound = false
        for (let i = sections.length - 1; i >= 0; i--) {
          const section = document.getElementById(sections[i])
          if (section) {
            const sectionTop = section.offsetTop
            const sectionBottom = sectionTop + section.offsetHeight
            
            // Check if we're within this section (with some margin)
            if (scrollPosition >= sectionTop - 100 && scrollPosition < sectionBottom - 100) {
              // Special handling: if we're in "home" section, clear active section
              if (sections[i] === 'home') {
                setActiveSection('')
              } else {
                setActiveSection(sections[i])
              }
              activeFound = true
              break
            }
          }
        }
        
        // If no section found and we're near the top, clear active section
        if (!activeFound && window.scrollY < 200) {
          setActiveSection('')
        }
      } else {
        setActiveSection('')
      }
    }
    
    window.addEventListener('scroll', handleScroll)
    handleScroll() // Check on mount
    return () => window.removeEventListener('scroll', handleScroll)
  }, [location.pathname])

  const handleNavClick = (e, sectionId) => {
    e.preventDefault()
    if (location.pathname !== '/') {
      // If not on home page, navigate to home first
      window.location.href = '/'
      setTimeout(() => {
        const element = document.getElementById(sectionId)
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' })
          // Set active section after scroll
          setTimeout(() => {
            setActiveSection(sectionId)
          }, 500)
        }
      }, 100)
    } else {
      const element = document.getElementById(sectionId)
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' })
        // Set active section after scroll
        setTimeout(() => {
          setActiveSection(sectionId)
        }, 500)
      }
    }
  }

  const navLinks = [
    { path: '/', label: 'Home', isHome: true },
    { sectionId: 'project-showcase', label: 'Project Showcase' },
    { sectionId: 'professional-journey', label: 'Professional Journey' },
    { sectionId: 'technical-skills', label: 'Technical Skills' },
  ]

  return (
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'glass shadow-lg' : 'bg-transparent'
      }`}
    >
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2 group">
            <motion.div
              whileHover={{ rotate: 360 }}
              transition={{ duration: 0.5 }}
            >
              <Code2 className="w-8 h-8 text-primary" />
            </motion.div>
            <span className="text-xl font-bold text-gradient use-my-font">Jarrad Totman</span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            {navLinks.map((link) => {
              if (link.isHome) {
                const handleHomeClick = (e) => {
                  if (location.pathname === '/') {
                    e.preventDefault()
                    window.scrollTo({ top: 0, behavior: 'smooth' })
                    setActiveSection('')
                  }
                }
                return (
                  <Link
                    key={link.path}
                    to={link.path}
                    onClick={handleHomeClick}
                    className={`relative px-3 py-2 text-sm font-medium transition-colors ${
                      location.pathname === link.path && !activeSection
                        ? 'text-primary'
                        : 'text-base-content/70 hover:text-primary'
                    }`}
                  >
                    {link.label}
                    {location.pathname === link.path && !activeSection && (
                      <motion.div
                        layoutId="navbar-indicator"
                        className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary"
                      />
                    )}
                  </Link>
                )
              } else {
                const isActive = activeSection === link.sectionId && location.pathname === '/'
                return (
                  <a
                    key={link.sectionId}
                    href={`#${link.sectionId}`}
                    onClick={(e) => handleNavClick(e, link.sectionId)}
                    className={`relative px-3 py-2 text-sm font-medium transition-colors ${
                      isActive
                        ? 'text-primary'
                        : 'text-base-content/70 hover:text-primary'
                    }`}
                  >
                    {link.label}
                    {isActive && (
                      <motion.div
                        layoutId="navbar-indicator"
                        className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary"
                      />
                    )}
                  </a>
                )
              }
            })}
          </div>

          {/* Social Links */}
          <div className="hidden md:flex items-center space-x-4">
            <a
              href="https://linkedin.com/in/jarradtotman"
              target="_blank"
              rel="noopener noreferrer"
              className="p-2 rounded-lg hover:bg-base-200 transition-colors"
              aria-label="LinkedIn"
            >
              <Linkedin className="w-5 h-5" />
            </a>
            <a
              href="mailto:jarradtotman@gmail.com"
              className="p-2 rounded-lg hover:bg-base-200 transition-colors"
              aria-label="Email"
            >
              <Mail className="w-5 h-5" />
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="md:hidden p-2 rounded-lg hover:bg-base-200 transition-colors"
            aria-label="Toggle menu"
          >
            {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden glass border-t border-base-300"
          >
            <div className="container mx-auto px-4 py-4 space-y-2">
              {navLinks.map((link) => {
                if (link.isHome) {
                  const handleMobileHomeClick = (e) => {
                    if (location.pathname === '/') {
                      e.preventDefault()
                      window.scrollTo({ top: 0, behavior: 'smooth' })
                      setActiveSection('')
                    }
                    setIsOpen(false)
                  }
                  return (
                    <Link
                      key={link.path}
                      to={link.path}
                      onClick={handleMobileHomeClick}
                      className={`block px-4 py-2 rounded-lg transition-colors ${
                        location.pathname === link.path && !activeSection
                          ? 'bg-primary text-primary-content'
                          : 'hover:bg-base-200'
                      }`}
                    >
                      {link.label}
                    </Link>
                  )
                } else {
                  const isActive = activeSection === link.sectionId && location.pathname === '/'
                  return (
                    <a
                      key={link.sectionId}
                      href={`#${link.sectionId}`}
                      onClick={(e) => {
                        handleNavClick(e, link.sectionId)
                        setIsOpen(false)
                      }}
                      className={`block px-4 py-2 rounded-lg transition-colors ${
                        isActive
                          ? 'bg-primary text-primary-content'
                          : 'hover:bg-base-200'
                      }`}
                    >
                      {link.label}
                    </a>
                  )
                }
              })}
              <div className="flex items-center space-x-4 pt-4 border-t border-base-300">
                <a
                  href="https://linkedin.com/in/jarradtotman"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 rounded-lg hover:bg-base-200"
                >
                  <Linkedin className="w-5 h-5" />
                </a>
                <a
                  href="mailto:jarradtotman@gmail.com"
                  className="p-2 rounded-lg hover:bg-base-200"
                >
                  <Mail className="w-5 h-5" />
                </a>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  )
}

export default Navbar
